import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Terminal from "../components/Terminal";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import {
    Box,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getRuntimeConfig } from "../runtimeConfig";
import { auth } from "../firebase";

const TASK_CHECK_BASE_URL = getRuntimeConfig("TERMINAL_HTTP_BASE_URL", "");

function getTaskCheckUrl(courseId, sessionSlug) {
    return `${TASK_CHECK_BASE_URL}/tasks/check/${encodeURIComponent(courseId)}/${encodeURIComponent(sessionSlug)}`;
}

function renderInlineText(value) {
    const text = String(value ?? "");
    const parts = text.split(/(`[^`]+`)/g).filter(Boolean);

    return parts.map((part, index) => {
        if (part.startsWith("`") && part.endsWith("`")) {
            return (
                <Box
                    key={index}
                    component="code"
                    sx={{
                        px: 0.6,
                        py: 0.2,
                        borderRadius: 1,
                        bgcolor: "rgba(79, 70, 229, 0.08)",
                        color: "primary.dark",
                        border: theme => `1px solid ${theme.palette.primary.light}`,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                        fontSize: "0.9em",
                        fontWeight: 700,
                        whiteSpace: "break-spaces"
                    }}
                >
                    {part.slice(1, -1)}
                </Box>
            );
        }

        if (index === 0 && part.startsWith("Goal:")) {
            return (
                <React.Fragment key={index}>
                    <Box component="strong" sx={{ color: "secondary.dark", fontWeight: 800 }}>
                        Goal:
                    </Box>
                    {part.slice("Goal:".length)}
                </React.Fragment>
            );
        }

        return <React.Fragment key={index}>{part}</React.Fragment>;
    });
}

function getLessonNote(line, index) {
    const text = String(line ?? "");

    if (text.startsWith("Goal:")) {
        return { label: "Goal", text: text.slice(5).trim(), tone: "primary" };
    }

    if (text.startsWith("Tip:") || text.startsWith("Remember:")) {
        return { label: "Remember", text: text.replace(/^(Tip|Remember):\s*/, ""), tone: "warning" };
    }

    if (text.startsWith("`") && text.includes("`")) {
        return { label: "Command", text, tone: "secondary" };
    }

    return { label: index === 1 ? "Why it matters" : "Good to know", text, tone: "default" };
}

function LessonNotes({ content }) {
    const lines = Array.isArray(content) ? content : [content];

    return (
        <Stack spacing={1.25}>
            {lines.filter(Boolean).map((line, index) => {
                const note = getLessonNote(line, index);
                return (
                    <Paper
                        key={index}
                        variant="outlined"
                        sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            borderLeft: 4,
                            borderLeftColor: note.tone === "default" ? "divider" : `${note.tone}.main`,
                            bgcolor: note.tone === "primary" ? "rgba(79, 70, 229, 0.04)" : "background.paper"
                        }}
                    >
                        <Typography
                            variant="overline"
                            color={note.tone === "default" ? "text.secondary" : `${note.tone}.dark`}
                            sx={{ display: "block", mb: 0.25, fontWeight: 800, lineHeight: 1.4 }}
                        >
                            {note.label}
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ lineHeight: 1.6 }}>
                            {renderInlineText(note.text)}
                        </Typography>
                    </Paper>
                );
            })}
        </Stack>
    );
}

function CoursePageStart({ courses }) {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDownMd = useMediaQuery(theme.breakpoints.down("md")); // mobile/tablet
    const { currentUser } = useAuth();

    // -------- Data safety --------
    const course = useMemo(
        () => (Array.isArray(courses) ? courses.find(c => c.id === courseId) : null),
        [courses, courseId]
    );
    const pages = course?.pages || []; // expecting [{title, content}, ...]
    const [currentPage, setCurrentPage] = useState(0);
    const pageSafe = Math.min(Math.max(currentPage, 0), Math.max(pages.length - 1, 0));
    const pageData = pages[pageSafe];
    const lessonTasks = Array.isArray(pageData?.tasks) ? pageData.tasks : [];
    const [checkingTasks, setCheckingTasks] = useState(false);
    const [taskCheckError, setTaskCheckError] = useState("");
    const [taskCheckResults, setTaskCheckResults] = useState(null);

    // stable session slug (no secrets here)
    const sessionSlug = useMemo(() => {
        const base = currentUser?.uid || currentUser?.email || "guest";
        return `${base}`;
    }, [currentUser, courseId]);

    // -------- Handlers --------
    const goPrev = useCallback(() => setCurrentPage(p => Math.max(0, p - 1)), []);
    const goNext = useCallback(
        () => setCurrentPage(p => Math.min(pages.length - 1, p + 1)),
        [pages.length]
    );
    const goTo = useCallback(
        idx => setCurrentPage(() => (idx >= 0 && idx < pages.length ? idx : 0)),
        [pages.length]
    );

    const checkLessonTasks = useCallback(async () => {
        if (lessonTasks.length === 0) return;

        setCheckingTasks(true);
        setTaskCheckError("");
        setTaskCheckResults(null);

        try {
            const idToken = await auth.currentUser?.getIdToken();
            if (!idToken) throw new Error("You must be logged in to check tasks.");
            const response = await fetch(getTaskCheckUrl(courseId, sessionSlug), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    lessonId: pageData?.id,
                    tasks: lessonTasks
                })
            });

            const data = await response.json().catch(() => null);
            if (!response.ok) {
                throw new Error(data?.error || `Task check failed with status ${response.status}`);
            }

            setTaskCheckResults(data);
        } catch (error) {
            setTaskCheckError(error.message || "Task check failed.");
        } finally {
            setCheckingTasks(false);
        }
    }, [courseId, lessonTasks, pageData?.id, sessionSlug]);

    useEffect(() => {
        setTaskCheckError("");
        setTaskCheckResults(null);
    }, [pageSafe]);

    // Keyboard navigation
    useEffect(() => {
        const onKey = e => {
            if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
            if (e.key === "ArrowLeft") goPrev();
            if (e.key === "ArrowRight") goNext();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [goPrev, goNext]);

    // Terminal expand toggle (useful on small screens)
    const [terminalExpanded, setTerminalExpanded] = useState(false);
    const [lessonsOpen, setLessonsOpen] = useState(!isDownMd);

    useEffect(() => {
        setLessonsOpen(!isDownMd);
    }, [courseId, isDownMd]);

    const courseDescription = course?.description ||
        `Work through ${course?.title || "this Linux course"} with guided lessons, an interactive terminal, and task validation.`;

    // -------- Empty states --------
    if (!courses) {
        return (
            <Box p={3}>
                <Typography variant="h6">No course data.</Typography>
                <Button sx={{ mt: 1 }} onClick={() => navigate(-1)}>Go Back</Button>
            </Box>
        );
    }
    if (!course) {
        return (
            <Box p={3}>
                <Typography variant="h6">Course not found.</Typography>
                <Button sx={{ mt: 1 }} onClick={() => navigate("/courses")}>All Courses</Button>
            </Box>
        );
    }
    if (pages.length === 0) {
        return (
            <Box p={3}>
                <Typography variant="h6">{course.title}</Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                    This course doesn’t have any pages yet.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1, sm: 2 }, height: "100%", boxSizing: "border-box" }}>
            <Seo
                title={`${course.title || "Course"} Lessons`}
                description={courseDescription}
                path={`/courses/${courseId}`}
                type="article"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Course",
                    "name": course.title || "NixDojo course",
                    "description": courseDescription,
                    "provider": {
                        "@type": "Organization",
                        "name": "NixDojo",
                        "sameAs": "https://nixdojo.com"
                    }
                }}
            />
            {/* Header */}
            <Box
                sx={{
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {course.title || "Course"}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title={lessonsOpen ? "Hide lessons" : "Show lessons"}>
                        <Button
                            onClick={() => setLessonsOpen(v => !v)}
                            variant="outlined"
                            size="small"
                            sx={{ whiteSpace: "nowrap" }}
                        >
                            {lessonsOpen ? "Hide lessons" : "Show lessons"}
                        </Button>
                    </Tooltip>
                    <Chip
                        size="small"
                        label={`Page ${pageSafe + 1} of ${pages.length}`}
                        variant="outlined"
                    />
                    <Tooltip title={terminalExpanded ? "Collapse terminal" : "Expand terminal"}>
                        <IconButton
                            onClick={() => setTerminalExpanded(v => !v)}
                            edge="end"
                            size="small"
                            aria-label={terminalExpanded ? "Collapse terminal" : "Expand terminal"}
                        >
                            {terminalExpanded ? "C" : "E"}
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            {/* Main layout */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: terminalExpanded
                            ? "minmax(0, 1fr)"
                            : lessonsOpen
                                ? "280px minmax(0, 1fr) minmax(360px, 1.05fr)"
                                : "56px minmax(0, 1fr) minmax(360px, 1.05fr)"
                    },
                    gap: 2,
                    alignItems: "stretch",
                    minHeight: "calc(100vh - 150px)"
                }}
            >
                {/* Left: Lesson sidebar */}
                {!terminalExpanded && (
                    <Paper
                        component="nav"
                        aria-label="Course lessons"
                        variant="outlined"
                        sx={{
                            display: { xs: lessonsOpen ? "block" : "none", md: "block" },
                            p: lessonsOpen ? 1 : 0.75,
                            position: "sticky",
                            top: 16,
                            height: "fit-content",
                            maxHeight: "calc(100vh - 180px)",
                            overflow: "hidden",
                            borderRadius: 2,
                            boxShadow: "0 18px 40px rgba(15, 23, 42, 0.05)",
                            transition: theme => theme.transitions.create(["width", "padding"], {
                                duration: theme.transitions.duration.shorter
                            })
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent={lessonsOpen ? "space-between" : "center"}
                            sx={{ mb: lessonsOpen ? 0.5 : 0 }}
                        >
                            {lessonsOpen && (
                                <Typography
                                    variant="subtitle2"
                                    sx={{ px: 1, py: 0.5, fontWeight: 700 }}
                                >
                                    Lessons
                                </Typography>
                            )}
                            <Tooltip title={lessonsOpen ? "Hide lessons" : "Show lessons"}>
                                <IconButton
                                    onClick={() => setLessonsOpen(v => !v)}
                                    size="small"
                                    aria-label={lessonsOpen ? "Hide lessons" : "Show lessons"}
                                >
                                    {lessonsOpen ? "H" : "L"}
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        {lessonsOpen ? (
                            <List dense disablePadding sx={{ maxHeight: "calc(100vh - 230px)", overflow: "auto" }}>
                                {pages.map((p, i) => (
                                    <ListItem key={i} disablePadding>
                                        <ListItemButton
                                            selected={i === pageSafe}
                                            onClick={() => goTo(i)}
                                            sx={{ borderRadius: 1 }}
                                        >
                                            <ListItemText
                                                primary={renderInlineText(p.title || `Page ${i + 1}`)}
                                                primaryTypographyProps={{
                                                    noWrap: true,
                                                    fontSize: 14
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Stack spacing={1} alignItems="center" sx={{ pt: 1 }}>
                                <Chip
                                    label={`${pageSafe + 1}/${pages.length}`}
                                    size="small"
                                    sx={{ writingMode: "vertical-rl", height: "auto", py: 1 }}
                                />
                            </Stack>
                        )}
                    </Paper>
                )}

                {/* Middle: Content */}
                <Paper
                    component="article"
                    variant="outlined"
                    sx={{
                        p: { xs: 2, sm: 3 },
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        borderRadius: 2,
                        minHeight: 320,
                        boxShadow: "0 18px 40px rgba(15, 23, 42, 0.05)"
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {renderInlineText(pageData?.title || `Page ${pageSafe + 1}`)}
                    </Typography>

                    <Divider />

                    <LessonNotes content={pageData?.content} />

                    {lessonTasks.length > 0 && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Divider />
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    Try it yourself
                                </Typography>
                                <Chip
                                    size="small"
                                    label={`${lessonTasks.length} task${lessonTasks.length === 1 ? "" : "s"}`}
                                    variant="outlined"
                                />
                            </Stack>

                            {lessonTasks.map((task, taskIndex) => {
                                const result = taskCheckResults?.tasks?.find(item => item.id === task.id);
                                const checks = Array.isArray(result?.checks) ? result.checks : [];

                                return (
                                    <Paper key={task.id || taskIndex} variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                                        <Stack spacing={1}>
                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {renderInlineText(task.title || `Task ${taskIndex + 1}`)}
                                                </Typography>
                                                {result && (
                                                    <Chip
                                                        size="small"
                                                        color={result.passed ? "success" : "error"}
                                                        label={result.passed ? "Complete" : "Needs work"}
                                                    />
                                                )}
                                            </Stack>
                                            <Box sx={{ p: 1.25, bgcolor: "action.hover", borderRadius: 1 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                                                    WHAT TO DO
                                                </Typography>
                                                <Typography variant="body2" component="div" sx={{ mt: 0.5, whiteSpace: "pre-wrap", lineHeight: 1.65 }}>
                                                    {renderInlineText(task.problem)}
                                                </Typography>
                                            </Box>
                                            {Array.isArray(task.hints) && task.hints.length > 0 && (
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                                                        NEED A HINT?
                                                    </Typography>
                                                    <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>
                                                    {task.hints.map((hint, hintIndex) => (
                                                        <Typography key={hintIndex} component="li" variant="body2" color="text.secondary">
                                                            {renderInlineText(hint)}
                                                        </Typography>
                                                    ))}
                                                    </Box>
                                                </Box>
                                            )}
                                            {checks.length > 0 && (
                                                <Stack spacing={0.75}>
                                                    {checks.map(check => (
                                                        <Typography
                                                            key={check.id}
                                                            variant="caption"
                                                            color={check.passed ? "success.main" : "error.main"}
                                                        >
                                                            {check.passed ? "Passed" : "Failed"}: {check.description || check.id}
                                                        </Typography>
                                                    ))}
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Paper>
                                );
                            })}

                            {taskCheckError && <Alert severity="error">{taskCheckError}</Alert>}
                            {taskCheckResults?.passed && (
                                <Alert severity="success">All lesson tasks are complete.</Alert>
                            )}

                            <Box>
                                <Button
                                    onClick={checkLessonTasks}
                                    disabled={checkingTasks}
                                    variant="contained"
                                >
                                    {checkingTasks ? (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CircularProgress size={16} color="inherit" />
                                            <span>Checking</span>
                                        </Stack>
                                    ) : (
                                        "Check lesson tasks"
                                    )}
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* Pager */}
                    <Stack direction="row" spacing={1.5} justifyContent="space-between" mt="auto">
                        <Button
                            onClick={goPrev}
                            disabled={pageSafe === 0}
                            variant="outlined"
                        >
                            Previous
                        </Button>
                        <Button
                            onClick={goNext}
                            disabled={pageSafe >= pages.length - 1}
                            variant="contained"
                        >
                            Next
                        </Button>
                    </Stack>
                </Paper>

                {/* Right: Terminal (stacks on mobile, can expand) */}
                <Paper
                    variant="outlined"
                    sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2,
                        height: { xs: terminalExpanded ? "60vh" : "50vh", md: "auto" },
                        minHeight: { md: 400 },
                        display: "flex",
                        flexDirection: "column",
                        boxShadow: "0 18px 40px rgba(15, 23, 42, 0.05)"
                    }}
                >
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Backend Terminal
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Box sx={{ flex: 1, minHeight: 300 }}>
                        <Terminal sessionSlug={sessionSlug} courseId={courseId} />
                    </Box>
                    {isDownMd && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            Tip: use the ⤢ icon to expand/collapse the terminal.
                        </Typography>
                    )}
                </Paper>
            </Box>
        </Box>
    );
}

export default CoursePageStart;
